import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../../stores/hooks";

import * as S from "./style";
import { t } from "i18next";
import {
  Button,
  Icon,
  Text,
  TextSizes,
  type Weight,
} from "@smartsuite/react-ui/lib";
import { Colors, GrayColors, TextColorNames } from "@smartsuite/types";
import { useCallback, useMemo } from "react";
import { navigateTo } from "../../../../../utils/navigate";

export const LimitsDisclamer100 = observer(() => {
  const { limits, config } = useRootStore();
  const canPlanBeUpgraded = useMemo(() => limits.canPlanBeUpgraded(), [limits]);

  const key = canPlanBeUpgraded
    ? "limits.100-disclamer-text-upgrade"
    : "limits.100-disclamer-text";

  const daysLeft = useMemo(() => {
    const today = new Date();
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
    );
    return lastDayOfMonth.getDate() - today.getDate();
  }, []);

  const navigateToPath = useCallback(
    (path: string) => {
      const origin = window.location.origin;
      const accountId = config.accountId;
      navigateTo(`${origin}/${accountId}/${path}`);
    },
    [config],
  );

  const onUsageClick = useCallback(() => {
    navigateToPath("account-administrator/usage");
  }, [navigateToPath]);

  const onUpgradeClick = useCallback(() => {
    navigateToPath("account-administrator/plans-billing");
  }, [navigateToPath]);

  const onContactSalesClick = useCallback(() => {
    window.open(
      "https://calendly.com/geoff-smartsuite/smartsuite-automations-and-apis?month=2025-05",
      "_blank",
      "noopener",
    );
  }, []);

  return (
    <S.Wrapper>
      <S.IconWrapper>
        <Icon name="important-lined" color={Colors.bRed} />
      </S.IconWrapper>

      <S.Disclamer>
        <S.Title>
          <Text weight="medium">{t("limits.reached-monthly-limit")}</Text>
        </S.Title>
        <S.Message>
          <Text>{t(key, { days: daysLeft })}</Text>
        </S.Message>
      </S.Disclamer>
      <S.ButtonContainer>
        <Button
          className="reached-limit-upgrade__button-usage"
          styles={{
            background: "white",
            textColor: TextColorNames.primary,
            height: 28,
            sidePadding: 7,
            textSize: TextSizes.S,
            borderRadius: 5,
            borderWidth: 1,
            textWeight: "regular" as Weight,
            iconColor: GrayColors["G-8"],
          }}
          iconName="database"
          iconSize="MISC"
          onClick={onUsageClick}
        >
          {t("limits.account-usage")}
        </Button>
        {canPlanBeUpgraded ? (
          <Button
            className="reached-limit-upgrade__button"
            iconName="upgrade-stars"
            styles={{
              background: Colors.bBlue,
              textColor: "white",
              height: 28,
              sidePadding: 7,
              textSize: TextSizes.S,
              borderRadius: 5,
              borderWidth: 1,
              textWeight: "medium" as Weight,
              iconRightIndent: 7,
              iconLeftIndent: 0,
              iconColor: Colors.bYellow,
            }}
            onClick={onUpgradeClick}
          >
            {t("limits.upgrade-now")}
          </Button>
        ) : (
          <Button
            className="reached-limit-upgrade__button-contact"
            styles={{
              background: "white",
              textColor: TextColorNames.primary,
              height: 28,
              sidePadding: 7,
              textSize: TextSizes.S,
              borderRadius: 5,
              borderWidth: 1,
              iconRightIndent: 7,
              iconLeftIndent: 0,
              textWeight: "regular" as Weight,
              iconColor: GrayColors["G-8"],
            }}
            iconName="open-in-new"
            onClick={onContactSalesClick}
          >
            {t("limits.contact-sale")}
          </Button>
        )}
      </S.ButtonContainer>
    </S.Wrapper>
  );
});
